import React from "react";
import { message, Tooltip } from "antd";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useUiLang } from "utils/uiLanguage";

/**
 * Кнопки, которые правят папку сами, в режиме «Без автоправок — только ручная работа».
 *
 * Правило режима: машина может ПОКАЗАТЬ — проверка, диагностика, предпросмотр, список
 * предложенных правок, — но не ЗАПИСАТЬ за переводчика ни перевод, ни озвучку, ни тайминги, ни
 * удаление. «Подготовить озвучку», «Пересоздать озвучку», ресинк, перевод папки, пересоздание
 * по оригиналу, «во все языки», исправление оригинала, «применить» в диагностике и
 * постобработке, прогон папки с автоисправлением — это автоправки, пусть и по одному нажатию:
 * каждая меняет десятки реплик, которых человек не трогал. Серверные ручки за большинством из
 * них флага не читают, поэтому выключены они здесь.
 *
 * Кнопка остаётся на месте и говорит, почему не работает. Кому она нужна — выключает режим
 * сам, в настройках, строкой «Режим работы».
 *
 * Выключение двойное: обёртка глотает нажатие и называет причину, а обработчик кнопки ещё раз
 * смотрит на `blocked` — окно, открытое до включения режима, тоже ничего не применит.
 */
export interface AutoEditBlock {
    blocked: boolean;
    reason: string;
}

export const autoEditBlockedReason = (en: boolean) =>
    en
        ? "Off in the “No automatic edits” mode: this button edits the folder by itself. Need it? Switch the mode off in Settings → Working mode."
        : "Выключено режимом „Без автоправок“: эта кнопка правит папку сама. Нужна — выключите режим в Настройках → Режим работы.";

/** Выключена ли сейчас машинная правка — по режиму пары, открытой на экране. */
export const useAutoEditBlock = (): AutoEditBlock => {
    const state = useAudioStateContext();
    const en = useUiLang() === "en";
    return { blocked: Boolean(state?.manualOnly), reason: autoEditBlockedReason(en) };
};

/**
 * Обёртка кнопки машинной правки. Режим выключен — ни одного лишнего узла: та же кнопка, что и
 * раньше. Включён — кнопка приглушена, подсказка называет причину, а нажатие (в том числе
 * касание на планшете, где подсказки по наведению нет) вместо действия говорит её же.
 */
export function AutoEditGate({ blocked, reason, children }: AutoEditBlock & { children: React.ReactNode }) {
    if (!blocked) return <>{children}</>;
    return (
        <Tooltip title={reason}>
            <span
                aria-disabled="true"
                data-auto-edit-blocked=""
                // Захват, а не всплытие: нажатие гасится до обработчика самой кнопки.
                onClickCapture={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    message.info(reason);
                }}
                style={{ display: "inline-flex", cursor: "not-allowed", opacity: 0.5 }}
            >
                {children}
            </span>
        </Tooltip>
    );
}
